import React, { useCallback, useState } from 'react'

import { updateFormData } from 'cng-common/features/form'
import { Translation } from 'cng-common/features/translations'
import { downloadBlob } from 'cng-common/helpers/io'
import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'
import { Button } from 'core-components/library/button'
import { NotificationController } from 'core-components/library/popup'
import { PartnerSearchFormField } from 'partner360/components/partnerSearchFormField'
import { ReportGeo } from 'partner360/components/reportGeo'
import { Text } from 'partner360/features/topPartnersManagement/components/text/Text'
import { TopPartnersManagementCard } from 'partner360/features/topPartnersManagement/components/topPartnersManagementCard/TopPartnersManagementCard'
import { IReportPartner } from 'partner360/models/report/IReportPartner'

import { exportTopPartners } from '../../api/exportTopPartners'
import { ITopPartnersFiltersFormData } from '../../models'
import { TRANSLATION_KEYS } from '../../translations'

import styles from './TopPartnersManagementFilterArea.module.scss'

interface ITopPartnersManagementFilterAreaProps {
  partners: IReportPartner[]
  modifiedFields: (string | number | symbol)[]
  initialFormData: ITopPartnersFiltersFormData
  formKey: string
}

export const TopPartnersManagementFilterArea = ({
  partners,
  modifiedFields,
  initialFormData,
  formKey,
}: ITopPartnersManagementFilterAreaProps) => {
  const hasModifiedValues = modifiedFields.length > 0
  const dispatch = useAppDispatch()
  const [isExporting, setIsExporting] = useState(false)

  const onExport = useCallback(async () => {
    setIsExporting(true)

    try {
      const blob = await exportTopPartners({ mappings: {} })
      downloadBlob('Top Partners.xlsx', blob)
    } catch {
      NotificationController.showError({
        message: 'Unable to export Top Partners. Please try again.',
      })
    } finally {
      setIsExporting(false)
    }
  }, [])

  const onClear = useCallback(() => {
    dispatch(
      updateFormData({
        formKey,
        formData: initialFormData,
      })
    )
  }, [dispatch, formKey, initialFormData])

  return (
    <TopPartnersManagementCard className={styles.card}>
      <Text type={'title'} text={TRANSLATION_KEYS.topPartners.filters.title} />

      <div className={styles.root}>
        <PartnerSearchFormField
          formKey={formKey}
          field="partnerId"
          defaultValue={initialFormData.partnerId}
          labelTranslationKey={TRANSLATION_KEYS.topPartners.filters.partnerId}
          tooltipTranslationKey={TRANSLATION_KEYS.topPartners.filters.partnerIdTooltip}
          partners={partners}
        />

        <ReportGeo
          geo={initialFormData.countryCode}
          field={'countryCode'}
          formKey={formKey}
          typeOfSelectedNodes={'onlyLeavesItems'}
          label={<Translation text={TRANSLATION_KEYS.topPartners.filters.geo} />}
          isCaptionsShortView
        />
      </div>
      <div className={styles.actions}>
        <Button variant={'text'} disabled={!hasModifiedValues} onClick={onClear}>
          <Translation text={TRANSLATION_KEYS.topPartners.filters.clear} />
        </Button>
        <Button onClick={onExport} disabled={isExporting} loading={isExporting}>
          <Translation text={TRANSLATION_KEYS.topPartners.filters.export} />
        </Button>
      </div>
    </TopPartnersManagementCard>
  )
}
